import React, { useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { ProgressBar } from '@/components/ui/loading';
import { ActionMenu } from '@/components/ui/menus';
import type { ActionMenuItem } from '@/components/ui/menus';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useConceptGenerationProgress } from '@/hooks/attendance/monthlyCuts/useConceptGenerationProgress';


export interface MonthlyCutProgressCellProps {
  monthlyCutId: number;
  // Ítems del menú "…" (Editar, Generar conceptos, etc.). Ya vienen filtrados por permiso.
  menuItems: ActionMenuItem[];
  // Señal de sesión: el usuario disparó la generación aquí y aún no finaliza. Da feedback inmediato
  // (arranque optimista) antes de que el listado refleje el estado.
  sessionActive?: boolean;
  // Contador monótono de disparos en sesión; al incrementar reinicia la barra (re-disparo).
  generationKey?: number;
  // Estado autoritativo del listado: el corte tiene una generación en curso. Hace visible la barra
  // cross-sesión, tras recargar y para otros usuarios, sin haberla disparado aquí.
  initiallyGenerating?: boolean;
  // % ya reportado por el listado, para sembrar la barra con el llenado real sin esperar al poll.
  initialProgressPercentage?: number;
  // Se invoca al finalizar el proceso: el padre refresca el listado y desmarca el corte como activo
  // (evita que al remontar la fila se vuelva a animar algo ya listo).
  onFinished?: () => void;
}

// Celda de progreso de generación de conceptos del listado de cortes mensuales (ASIS-4969).
// - Fila con error   → mensaje de error en la fila.
// - Fila en proceso  → barra de progreso verde con el porcentaje de avance.
// - En otro caso     → menú "…" de acciones (si hay ítems visibles).
// El error se evalúa ANTES que el estado activo: al finalizar con error, el hook mantiene
// isActive=true durante COMPLETION_HOLD_MS, así que revisar isActive primero mostraría la barra
// (éxito) ~1.2s antes de saltar al error. Con el error primero, el mensaje aparece de inmediato.
// La barra arranca por sesión (disparada aquí) o por el estado del listado (`initiallyGenerating`,
// autoritativo para recarga / otros usuarios). El polling dedicado toma el control al arrancar y
// provee el % en vivo, la finalización y el error.
export const MonthlyCutProgressCell: React.FC<MonthlyCutProgressCellProps> = ({
  monthlyCutId,
  menuItems,
  sessionActive = false,
  generationKey = 0,
  initiallyGenerating = false,
  initialProgressPercentage,
  onFinished,
}) => {
  const { t: translate } = useTranslation();
  const enabled = sessionActive || initiallyGenerating;
  // La semilla del % solo aplica cuando la barra arranca por el listado; en un re-disparo en sesión
  // se parte de 0 para no reflejar el % del run anterior.
  const seedPercentage = sessionActive ? undefined : initialProgressPercentage;
  const { progress, isActive } = useConceptGenerationProgress(
    monthlyCutId,
    enabled,
    generationKey,
    onFinished,
    seedPercentage,
  );

  const hasError = progress?.hasError ?? false;
  const progressPercentage = progress?.progressPercentage ?? 0;
  const errorMessage = useMemo(
    () => progress?.errorMessage ?? translate('attendance:monthlyCutsList.conceptGeneration.errors.processFailed'),
    [progress?.errorMessage, translate],
  );

  if (hasError) {
    return (
      <Text
        variant="small-regular"
        color={colors.feedback.error400}
        data-testid="monthly-cut-error-message"
      >
        {errorMessage}
      </Text>
    );
  }

  if (isActive) {
    // Mientras el avance es 0 (job en cola / sin batches completados), se pulsa la barra para
    // comunicar actividad; al haber avance real, se muestra el llenado sin el pulso del contenedor.
    const isWaiting = progressPercentage === 0;
    // La barra llena el ancho de la celda (w-full, sin tope) para que sea responsiva y no quede
    // pequeña con márgenes; se centra sola al ocupar todo el ancho disponible de la columna.
    return (
      <div className={`w-full ${isWaiting ? 'animate-pulse' : ''}`} data-testid="monthly-cut-progress-cell">
        <ProgressBar
          value={progressPercentage}
          animated
          data-testid="monthly-cut-progress-bar"
        />
      </div>
    );
  }

  if (menuItems.length === 0) return null;

  return <ActionMenu items={menuItems} />;
};

export default MonthlyCutProgressCell;
